/**
 * Google Tag Manager integration for Design Studio
 */

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer: Array<Record<string, unknown>>
  }
}

// Initialize dataLayer if it doesn't exist
if (typeof window !== 'undefined' && !window.dataLayer) {
  window.dataLayer = []
}

/**
 * Push an event to the GTM data layer
 */
export const pushToDataLayer = (data: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data)
  }
}

/**
 * Track a GTM interaction event
 */
export const trackGtmEvent = (
  eventName: string,
  gtmElement: string,
  gtmLabel?: string,
  additionalData?: Record<string, unknown>,
) => {
  pushToDataLayer({
    event: eventName,
    gtm_element: gtmElement,
    gtm_label: gtmLabel,
    timestamp: new Date().toISOString(),
    ...additionalData,
  })
}

/**
 * Track design studio specific events
 */
export const trackDesignEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
) => {
  pushToDataLayer({
    event: 'design_studio_event',
    event_category: category,
    event_action: action,
    event_label: label,
    event_value: value,
  })
}

/**
 * Track element interactions (add, update, delete)
 */
export const trackElementInteraction = (
  action: 'add' | 'update' | 'delete' | 'select' | 'deselect',
  elementType: 'text' | 'image' | 'asset' | 'shape',
  elementId?: string,
) => {
  trackDesignEvent(action, 'element_interaction', elementType, undefined)
  pushToDataLayer({
    element_type: elementType,
    element_id: elementId,
  })
}

/**
 * Track save events
 */
export const trackSaveEvent = (
  status: 'start' | 'success' | 'error',
  designId: string,
  elementCount?: number,
) => {
  trackDesignEvent(`save_${status}`, 'save', designId, elementCount)
}

/**
 * Track toolbar button clicks
 */
export const trackToolbarClick = (buttonId: string, label?: string) => {
  trackGtmEvent('toolbar_click', buttonId, label, {
    event_category: 'toolbar',
    event_action: 'click',
  })
}

/**
 * Initialize GTM click tracking for all data-gtm elements
 */
export const initializeGtmClickTracking = () => {
  if (typeof window === 'undefined') return

  // Use event delegation for better performance
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    const gtmElement = target.closest('[data-gtm]') as HTMLElement

    if (gtmElement) {
      const gtmId = gtmElement.getAttribute('data-gtm')
      const gtmLabel = gtmElement.getAttribute('data-gtm-label') || gtmElement.textContent?.trim()

      if (gtmId) {
        trackGtmEvent('gtm_click', gtmId, gtmLabel || undefined)
      }
    }
  })

  // Track page view
  pushToDataLayer({
    event: 'design_studio_loaded',
    page_path: window.location.pathname,
    page_search: window.location.search,
  })
}

/**
 * Track design loading
 */
export const trackDesignLoad = (designId: string, productId?: string, elementCount?: number) => {
  trackDesignEvent('design_loaded', 'design', designId, elementCount)
  pushToDataLayer({
    design_id: designId,
    product_id: productId,
    element_count: elementCount,
  })
}

/**
 * Track errors
 */
export const trackError = (errorType: string, errorMessage: string, errorContext?: string) => {
  pushToDataLayer({
    event: 'design_studio_error',
    error_type: errorType,
    error_message: errorMessage,
    error_context: errorContext,
  })
}
